'use client';

import type { CustomFetchOptions, ExtendedResponse, InterceptorConfig, IRouterInstance, MonkeyFetchFunc } from './types';

type ErrorHandlers = (InterceptorConfig['responseErrorHandlers'] | undefined)[];
type ErrorHandlerKey = keyof NonNullable<InterceptorConfig['responseErrorHandlers']>;
type HttpErrorHandlerKey = Exclude<ErrorHandlerKey, 'aborted' | 'unknown'>;

const handlerLookup: Record<number, HttpErrorHandlerKey> = {
  400: 'badRequest',
  401: 'unauthorized',
  403: 'forbidden',
  404: 'notFound',
  409: 'conflict',
  429: 'tooManyRequests',
};

// `{ retry: true }` is only honoured on the original attempt — monkeyFetch retries at most once.
const handleResponseError = async (args: {
  router: IRouterInstance;
  handlers: ErrorHandlers;
  response: ExtendedResponse | null;
  input: RequestInfo | URL;
  init: RequestInit;
  error: Error | null;
  isRetry: boolean;
}): Promise<{ retry: boolean }> => {
  const { router, handlers, response, error, init, input, isRetry } = args;
  for (const handlerGroup of handlers) {
    if (!handlerGroup) {
      continue;
    }

    if (error?.name === 'AbortError') {
      for (const handlerGroup of handlers) {
        if (!handlerGroup?.aborted) continue;
        handlerGroup.aborted(error, input, init);
        return { retry: false }; // Short circuit - abort error was handled
      }
    }

    if (response !== null && response.status in handlerLookup) {
      const handlerKey = handlerLookup[response.status];
      const handler = handlerGroup[handlerKey];
      if (handler) {
        const result = await handler({ response, router, isRetry });
        if (result && typeof result === 'object' && 'retry' in result && result.retry) {
          return { retry: true };
        }
        if (!result || (typeof result === 'object' && 'shortCircuit' in result && result.shortCircuit)) {
          return { retry: false };
        }
      }
    } else {
      // Unknown status code or response is null
      const handler = handlerGroup.unknown;
      if (handler) {
        const result = await handler({ response, router });
        if (!result || (typeof result === 'object' && result.shortCircuit)) {
          return { retry: false };
        }
      }
    }
  }
  return { retry: false };
};

export function registerMonkeyFetch(router: IRouterInstance, interceptors: InterceptorConfig[] = []) {
  const originalFetch: MonkeyFetchFunc = window.fetch;

  async function monkeyFetch<T = unknown>(input: RequestInfo | URL, init?: CustomFetchOptions<T>): Promise<ExtendedResponse<T>> {
    let modifiedInput = input;
    let modifiedInit = { ...init };

    const url = input instanceof URL ? input.href : typeof input === 'string' ? input : input.url;

    const matchedInterceptors = interceptors.filter(({ matcher }) => {
      const matchers = Array.isArray(matcher) ? matcher : [matcher];
      return matchers.some((regex) => regex.test(url));
    });

    // If no interceptors match, just perform the original fetch
    if (matchedInterceptors.length === 0) {
      return originalFetch(input, init);
    }

    const requestInterceptors = matchedInterceptors.map(({ requestInterceptor }) => requestInterceptor).filter(Boolean);

    const responseInterceptors = matchedInterceptors.map(({ responseInterceptor }) => responseInterceptor).filter(Boolean);

    const requestErrorHandlers = matchedInterceptors.map(({ requestErrorHandler }) => requestErrorHandler).filter(Boolean);

    const globalResponseErrorHandlers = matchedInterceptors.map(({ responseErrorHandlers }) => responseErrorHandlers).filter(Boolean);

    const localResponseErrorHandlers = init?.responseErrorHandlers ? [init.responseErrorHandlers] : [];

    const responseErrorHandlers = [...localResponseErrorHandlers, ...globalResponseErrorHandlers];

    const onSuccessHandler = init?.onSuccess;

    // Apply request interceptors
    for (const interceptor of requestInterceptors) {
      try {
        if (interceptor) {
          const result = await interceptor(url, modifiedInit);
          modifiedInput = result.url;
          modifiedInit = result.options;
        }
      } catch (error) {
        for (const handler of requestErrorHandlers) {
          handler?.(error as Error, modifiedInput, modifiedInit);
        }
      }
    }

    const runResponseInterceptors = async (response: ExtendedResponse) => {
      let result = response;
      for (const interceptor of responseInterceptors) {
        if (interceptor) {
          result = await interceptor(result.clone());
        }
      }
      return result;
    };

    const doFetch = async () => runResponseInterceptors(await originalFetch(modifiedInput, modifiedInit));

    const dispatchResponseError = (args: { response: ExtendedResponse | null; error: Error | null; isRetry: boolean }) =>
      handleResponseError({ router, handlers: responseErrorHandlers, init: modifiedInit, input: modifiedInput, ...args });

    try {
      let response = await doFetch();

      if (!response.ok) {
        const { retry } = await dispatchResponseError({ response: response.clone(), error: null, isRetry: false });

        if (retry) {
          // Handler judged the failure stale (e.g. cross-tab session refresh) — re-issue once for the original caller.
          response = await doFetch();
          if (!response.ok) {
            await dispatchResponseError({ response: response.clone(), error: null, isRetry: true });
          }
        }
      }

      // Check ok once on the final response so a handler-healed 401 still fires onSuccess.
      if (response.ok && onSuccessHandler) {
        await onSuccessHandler({ router, response: response.clone() });
      }

      return response;
    } catch (error) {
      await dispatchResponseError({ response: null, error: error as Error, isRetry: false });
      throw error;
    }
  }

  // Monkey-patch the global fetch function if on client side
  if (typeof window !== 'undefined') {
    window.fetch = monkeyFetch as typeof window.fetch;
  }

  return {
    unregister: () => {
      window.fetch = originalFetch as typeof window.fetch;
    },
  };
}
